import { memo, useState, ChangeEvent } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Error, TextInput } from "@narmi/design_system";
import { ContentCard } from "cerulean";
import { entities, modules } from "byzantine";

import { TEMPLATES_ROUTE } from "../../../Templates/constants";
import TemplateListCard from "./TemplateListCard";
import styles from "./InternationalWire.module.scss";

type TemplateSelectorProps = {
  onSelectTemplate?: (id: API.SwiftWireTemplateId) => void;
  selectedTemplateId?: API.SwiftWireTemplateId;
  error?: string;
};

function TemplateSelector({
  onSelectTemplate,
  selectedTemplateId,
  error,
}: TemplateSelectorProps) {
  const { l10n } = useLocalization();
  const [searchQuery, setSearchQuery] = useState("");

  const { watch } = modules.internationalWires.useInternationalWireContext();
  const currency = watch("recipientCurrency");

  const { hasManageIntlWireTemplatesPermission } =
    modules.wireTemplates.useWirePermissions();

  const allTemplateIds =
    entities.swiftwireTemplates.useSortedSwiftWireTemplateIds();
  const currencyMatchingTemplateIds =
    modules.internationalWires.useFilteredSwiftWireTemplateIds(currency, "");
  const filteredTemplateIds =
    modules.internationalWires.useFilteredSwiftWireTemplateIds(
      currency,
      searchQuery,
    );

  const hasTemplates = allTemplateIds.length > 0;
  const hasCurrencyTemplates = currencyMatchingTemplateIds.length > 0;
  const hasResults = filteredTemplateIds.length > 0;

  const templateListEmpty = () => (
    <>
      {hasManageIntlWireTemplatesPermission ? (
        <p className="margin--top--xs">
          {l10n.getString("international-wire-message-no-templates")}
        </p>
      ) : (
        <>
          <p className="margin--top--xs">
            {l10n.getString(
              "international-wire-message-no-templates-no-permission-1",
            )}
          </p>
          <p className="margin--top--xs">
            {l10n.getString(
              "international-wire-message-no-templates-no-permission-2",
            )}
          </p>
        </>
      )}
      {hasManageIntlWireTemplatesPermission && (
        <div className="margin--top--l">
          <Button
            label={l10n.getString("button-setup-template")}
            kind="primary"
            onClick={() =>
              window.location.assign(
                `/${TEMPLATES_ROUTE}/international_wire?from=template-selector`,
              )
            }
            size="m"
          />
        </div>
      )}
    </>
  );

  const templateListEmptyForCurrency = () => (
    <>
      {hasManageIntlWireTemplatesPermission ? (
        <p className="margin--top--xs">
          {l10n.getString(
            "international-wire-message-no-templates-for-currency",
          )}
        </p>
      ) : (
        <>
          <p className="margin--top--xs">
            {l10n.getString(
              "international-wire-message-no-templates-no-permission-1",
            )}
          </p>
          <p className="margin--top--xs">
            {l10n.getString(
              "international-wire-message-no-templates-no-permission-2",
            )}
          </p>
        </>
      )}
      {hasManageIntlWireTemplatesPermission && (
        <div className="margin--top--l">
          <Button
            label={l10n.getString("button-setup-template")}
            kind="primary"
            onClick={() =>
              window.location.assign(
                `/${TEMPLATES_ROUTE}/international_wire?from=template-selector`,
              )
            }
            size="m"
          />
        </div>
      )}
    </>
  );

  const searchBar = () => (
    <div className={styles.templateSearchBar}>
      <TextInput
        startIcon="search"
        showClearButton
        placeholder={l10n.getString("placeholder-search-templates")}
        value={searchQuery}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setSearchQuery(e.target.value || "")
        }
      />
    </div>
  );

  const templateNoResults = () => (
    <p className={styles.templateNoResults}>
      {l10n.getString("message-search-no-results")}
    </p>
  );

  const templateList = () => (
    <div className={styles.templateList}>
      {filteredTemplateIds.map((templateId) => (
        <TemplateListCard
          key={templateId}
          templateId={templateId}
          onSelect={onSelectTemplate}
          selected={templateId === selectedTemplateId}
        />
      ))}
    </div>
  );

  return (
    <ContentCard kind="bordered">
      <div className={styles.templateSelectorCard}>
        <div className={styles.templateSelectorHeader}>
          <strong>{l10n.getString("heading-wire-template-selector")}</strong>
          {hasCurrencyTemplates && hasManageIntlWireTemplatesPermission && (
            <Button
              label={l10n.getString("button-manage-templates")}
              kind="plain"
              onClick={() =>
                window.location.assign(
                  `/${TEMPLATES_ROUTE}/international_wire?from=template-selector`,
                )
              }
              size="s"
            />
          )}
        </div>
        {!hasTemplates && templateListEmpty()}
        {hasTemplates &&
          !hasCurrencyTemplates &&
          templateListEmptyForCurrency()}
        {hasCurrencyTemplates && searchBar()}
        {hasCurrencyTemplates && (
          <div className={styles.templateSelectorScrollableArea}>
            {hasResults && templateList()}
            {!hasResults && templateNoResults()}
          </div>
        )}
      </div>
      <Error error={error} marginTop="s" />
    </ContentCard>
  );
}

export default memo(TemplateSelector);
